import { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw, Copy, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { captureError } from '@/lib/sentry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Optional label for identifying which boundary caught the error */
  boundaryName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showStack: boolean;
}

const isDev = import.meta.env.DEV;

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    showStack: false,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    
    // Enhanced logging with context
    const context = {
      boundaryName: this.props.boundaryName || 'Global',
      route: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
      timestamp: new Date().toISOString(),
      componentStack: errorInfo.componentStack,
    };
    
    console.error(`[ErrorBoundary:${context.boundaryName}] Caught error at ${context.route}:`, error);
    console.error('Component stack:', errorInfo.componentStack);

    // Report to Sentry
    captureError(error, context);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, showStack: false });
  };

  private toggleStack = () => {
    this.setState(prev => ({ showStack: !prev.showStack }));
  };

  private copyErrorDetails = () => {
    const { error, errorInfo } = this.state;
    const details = [
      `Error: ${error?.message || 'Unknown error'}`,
      `Route: ${window.location.pathname}`,
      `Timestamp: ${new Date().toISOString()}`,
      `Boundary: ${this.props.boundaryName || 'Global'}`,
      '',
      'Stack Trace:',
      error?.stack || 'No stack trace',
      '',
      'Component Stack:',
      errorInfo?.componentStack || 'No component stack',
    ].join('\n');

    navigator.clipboard.writeText(details).then(() => {
      toast.success('Error details copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy error details');
    });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorInfo, showStack } = this.state;

      return (
        <div className="min-h-[400px] flex items-center justify-center p-4">
          <Card className="max-w-lg w-full border-destructive/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                Something went wrong
                {this.props.boundaryName && isDev && (
                  <span className="text-xs font-normal text-muted-foreground ml-2">
                    ({this.props.boundaryName})
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                An error occurred while loading this page. Please try refreshing.
              </p>
              
              {/* Error message */}
              {error && (
                <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-32">
                  {error.message}
                </pre>
              )}

              {/* Dev-only: Stack trace (collapsible) */}
              {isDev && error?.stack && (
                <div className="space-y-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={this.toggleStack}
                    className="text-xs text-muted-foreground p-0 h-auto"
                  >
                    {showStack ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
                    {showStack ? 'Hide' : 'Show'} Stack Trace
                  </Button>
                  
                  {showStack && (
                    <div className="space-y-2">
                      <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-48 whitespace-pre-wrap">
                        {error.stack}
                      </pre>
                      {errorInfo?.componentStack && (
                        <>
                          <p className="text-xs text-muted-foreground font-medium">Component Stack:</p>
                          <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-32 whitespace-pre-wrap">
                            {errorInfo.componentStack}
                          </pre>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button onClick={this.handleReset} variant="outline" size="sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
                <Button onClick={() => window.location.reload()} size="sm">
                  Refresh Page
                </Button>
                {isDev && (
                  <Button onClick={this.copyErrorDetails} variant="ghost" size="sm">
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Details
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
